import React from 'react';
import { useMount } from '@shared/utils/hooks/useMount';
import { useHotelBookingGuestSiteTelemetry } from '../../HotelBooking.telemetry';
import { JoyLogoLoader } from '@shared/components/JoyLogoLoader';
import { useJoyPlaceBySourceIdQuery } from './HotelDetails.graphql.generated';
import { PartnerHotelRedirect } from '../../components/PartnerHotelRedirect';
import { useEventInfo } from '@shared/utils/eventInfo';
import { usePartnerHotelQueryParams } from '../../hooks/usePartnerHotelQueryParams';
import { useHotelBookingRedirectHealth, wrapWithHotelBookingRedirectProvider } from '../../HotelBooking.health';
import { SearchParamsObserver } from '@shared/components/SearchParamsObserver/SearchParamsObserver';
import { useTravelPlacesPricingAndBookingDetails } from '@shared/hooks/useTravelPlacesPricingAndBookingDetails/useTravelPlacesPricingAndBookingDetails';
import { useHotelBookingRedirectOverrides } from '../../hooks/useHotelBookingRedirectOverrides';
import { PartnerSourceIdType } from '../../HotelBooking.types';
import { useIsFeatureEnabled } from '@shared/core/featureFlags';
import { isBotClient } from '@shared/utils/botDetection/isBotClient';

export const HotelDetails = wrapWithHotelBookingRedirectProvider(({ joyPlaceId: joyPlaceIdArg }: { joyPlaceId: string; eventHandle: string }) => {
  const { searchParams, handleSearchParamsUpdate, redirectOverrides, skipIfNotValidSearchParams } = useHotelBookingRedirectOverrides();
  useHotelBookingRedirectHealth(); // Call health tracking hook to opt out of auto `loadComplete` fallback.
  const telemetry = useHotelBookingGuestSiteTelemetry();
  const { travelPlaceId, sourceType, source, queryParamEventId } = usePartnerHotelQueryParams(joyPlaceIdArg);

  const { eventInfo, loading: loadingEventInfo } = useEventInfo();
  const eventDate = eventInfo?.finalizedDate?.dateString || undefined;
  const eventId = eventInfo?.eventId || queryParamEventId;

  const { data: tripProfitJoyPlaceData, loading: loadingTripProfitJoyPlaceData } = useJoyPlaceBySourceIdQuery({
    variables: {
      payload: { sourceId: travelPlaceId!, sourceType: PartnerSourceIdType[sourceType] },
    },
    batchMode: 'fast',
    skip: source !== 'tripprofit' || !travelPlaceId || isBotClient(),
  });

  const { data: joyPlaceData, loading: loadingJoyPlaceData } = useTravelPlacesPricingAndBookingDetails(
    travelPlaceId ? [travelPlaceId] : [],
    'joyPlace',
    source === 'tripprofit',
    redirectOverrides,
    skipIfNotValidSearchParams,
  );

  const redirectMechanisms = joyPlaceData?.travelPlacesPricingAndBookingDetails?.joyPlaces?.[0]?.redirectMechanisms;

  const [isTripProfitOptedOut, isTripProfitOptedOutQueryLoading] = useIsFeatureEnabled({ consentKey: 'websiteOptOutTripProfit' });

  const isLoading = loadingJoyPlaceData || loadingEventInfo || isTripProfitOptedOutQueryLoading || loadingTripProfitJoyPlaceData;

  const joyPlace = source === 'tripprofit' ? tripProfitJoyPlaceData?.joyPlaceBySourceId : joyPlaceData?.travelPlacesPricingAndBookingDetails?.joyPlaces?.[0]?.joyPlace;
  const googleMaps = { gplid: joyPlace?.googlePlaceId, name: joyPlace?.name, address: joyPlace?.address, lat: joyPlace?.lat, lng: joyPlace?.long };

  useMount(() => {
    telemetry.hotelDetails.enter();
  });

  return (
    <>
      <SearchParamsObserver onValueUpdate={handleSearchParamsUpdate} />
      {isLoading ? (
        <JoyLogoLoader loaderKey="guest-hotel-booking-accommodations-place" />
      ) : (
        <PartnerHotelRedirect
          pageContext="HotelDetails"
          telemetryName="guestPartnerHotelDetailsRedirect"
          travelPlace={{
            id: travelPlaceId,
            name: joyPlace?.name,
            url: joyPlace?.url,
            type: 'joyPlace',
          }}
          searchParams={searchParams}
          redirectMechanisms={redirectMechanisms}
          googleMaps={googleMaps}
          eventData={{ eventId, eventDate }}
          forceRedirectToHotelWebsite={isTripProfitOptedOut}
        />
      )}
    </>
  );
});
